import { HouseholdRelationship } from '@access-management/access-management';
import { InvitationResponseV1, InvitationStatus } from '@access-management/invitation';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';

import { InvitationService } from '@core/services/invitation';

@Component({
  selector: 'app-invitations-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    DialogModule,
    SelectModule,
    IconField,
    InputIcon,
    InputTextModule
  ],
  templateUrl: './invitations-table.html',
  styleUrl: './invitations-table.scss'
})
export class InvitationsTableComponent {
  private readonly invitationService = inject(InvitationService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  readonly invitationsResource = this.invitationService.createInvitationsResource();

  readonly searchTerm = signal<string>('');
  readonly statusFilter = signal<InvitationStatus | null>(null);
  readonly householdRelationshipFilter = signal<HouseholdRelationship | null>(null);
  readonly groupFilter = signal<string>('');
  readonly inviterFilter = signal<string>('');

  readonly showDetailDialog = signal<boolean>(false);
  readonly selectedInvitation = signal<InvitationResponseV1 | null>(null);

  readonly showUpdateEmailDialog = signal<boolean>(false);
  readonly newEmail = signal<string>('');
  readonly processingAction = signal<boolean>(false);

  readonly statusOptions = signal([
    { label: 'All Statuses', value: null },
    { label: 'Pending Purchase', value: InvitationStatus.PendingPurchase },
    { label: 'Pending', value: InvitationStatus.Pending },
    { label: 'Accepted', value: InvitationStatus.Accepted },
    { label: 'Declined', value: InvitationStatus.Declined },
    { label: 'Expired', value: InvitationStatus.Expired },
    { label: 'Cancelled', value: InvitationStatus.Cancelled }
  ]);

  readonly householdRelationshipOptions = signal([
    { label: 'All Household Relationships', value: null },
    { label: 'Owner', value: HouseholdRelationship.Owner },
    { label: 'Member', value: HouseholdRelationship.Member },
    { label: 'Dependent', value: HouseholdRelationship.Dependent }
  ]);

  readonly columns = signal<{ field: string; header: string; sortable: boolean }[]>([
    { field: 'inviteeEmail', header: 'Invitee Email', sortable: true },
    { field: 'inviterName', header: 'Inviter', sortable: true },
    { field: 'groupName', header: 'Group', sortable: true },
    { field: 'status', header: 'Status', sortable: true },
    { field: 'householdRelationship', header: 'Household Relationship', sortable: true },
    { field: 'expiresOn', header: 'Expires', sortable: true },
    { field: 'createdOn', header: 'Created', sortable: true },
    { field: 'actions', header: 'Actions', sortable: false }
  ]);

  readonly filteredInvitations = computed<InvitationResponseV1[]>(() => {
    const search = this.searchTerm().toLowerCase();
    const status = this.statusFilter();
    const householdRelationship = this.householdRelationshipFilter();
    const groupQuery = this.groupFilter().toLowerCase();
    const inviterQuery = this.inviterFilter().toLowerCase();
    const invitations: InvitationResponseV1[] = this.invitationsResource.value() ?? [];

    return invitations.filter((inv) => {
      if (status && inv.status !== status) return false;
      if (householdRelationship && inv.householdRelationship !== householdRelationship) return false;
      if (groupQuery && !this.matchesGroup(inv, groupQuery)) return false;
      if (inviterQuery && !this.matchesInviter(inv, inviterQuery)) return false;
      if (search && !this.matchesSearch(inv, search)) return false;
      return true;
    });
  });

  readonly isLoading = computed(() => this.invitationsResource.isLoading());

  readonly canCancelOrEditEmail = computed(() => {
    const inv = this.selectedInvitation();
    return inv ? inv.status === InvitationStatus.PendingPurchase || inv.status === InvitationStatus.Pending : false;
  });

  readonly canResend = computed(() => {
    const inv = this.selectedInvitation();
    return inv ? inv.status === InvitationStatus.Pending || inv.status === InvitationStatus.Expired : false;
  });

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
  }

  onGroupFilterChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.groupFilter.set(value);
  }

  onInviterFilterChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.inviterFilter.set(value);
  }

  onStatusFilterChange(value: InvitationStatus | null): void {
    this.statusFilter.set(value);
  }

  onHouseholdRelationshipFilterChange(value: HouseholdRelationship | null): void {
    this.householdRelationshipFilter.set(value);
  }

  onClearFilters(): void {
    this.searchTerm.set('');
    this.statusFilter.set(null);
    this.householdRelationshipFilter.set(null);
    this.groupFilter.set('');
    this.inviterFilter.set('');
  }

  onViewInvitation(invitation: InvitationResponseV1): void {
    this.selectedInvitation.set(invitation);
    this.showDetailDialog.set(true);
  }

  onRefresh(): void {
    this.invitationsResource.reload();
    this.messageService.add({ severity: 'info', summary: 'Refreshing', detail: 'Reloading invitations...' });
  }

  async onResend(): Promise<void> {
    const inv = this.selectedInvitation();
    if (!inv) return;
    this.processingAction.set(true);
    try {
      const updated = await firstValueFrom(
        this.invitationService.resendInvitation(inv.inviterUserId, inv.invitationId)
      );
      this.selectedInvitation.set(updated);
      this.messageService.add({
        severity: 'success',
        summary: 'Resent',
        detail: `Invitation re-sent to ${updated.inviteeEmail}`
      });
      this.invitationsResource.reload();
    } catch (error) {
      this.messageService.add({ severity: 'error', summary: 'Resend failed', detail: this.errorText(error) });
    } finally {
      this.processingAction.set(false);
    }
  }

  onCancelInvitation(): void {
    const inv = this.selectedInvitation();
    if (!inv) return;
    this.confirmationService.confirm({
      message: `Cancel the invitation to ${inv.inviteeEmail}? This cannot be undone.`,
      header: 'Cancel Invitation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.cancelInvitation(inv)
    });
  }

  private async cancelInvitation(inv: InvitationResponseV1): Promise<void> {
    this.processingAction.set(true);
    try {
      const updated = await firstValueFrom(
        this.invitationService.cancelInvitation(inv.inviterUserId, inv.invitationId)
      );
      this.selectedInvitation.set(updated);
      this.messageService.add({
        severity: 'success',
        summary: 'Cancelled',
        detail: `Invitation to ${updated.inviteeEmail} cancelled`
      });
      this.invitationsResource.reload();
    } catch (error) {
      this.messageService.add({ severity: 'error', summary: 'Cancel failed', detail: this.errorText(error) });
    } finally {
      this.processingAction.set(false);
    }
  }

  onOpenUpdateEmail(): void {
    const inv = this.selectedInvitation();
    if (!inv) return;
    this.newEmail.set(inv.inviteeEmail);
    this.showUpdateEmailDialog.set(true);
  }

  onUpdateEmailInput(event: Event): void {
    this.newEmail.set((event.target as HTMLInputElement).value);
  }

  isEmailValid(): boolean {
    const value = this.newEmail().trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  async onSaveNewEmail(): Promise<void> {
    const inv = this.selectedInvitation();
    if (!inv || !this.isEmailValid()) return;
    this.processingAction.set(true);
    try {
      const updated = await firstValueFrom(
        this.invitationService.updateInvitationEmail(inv.inviterUserId, inv.invitationId, this.newEmail().trim())
      );
      this.selectedInvitation.set(updated);
      this.showUpdateEmailDialog.set(false);
      this.messageService.add({
        severity: 'success',
        summary: 'Email updated',
        detail: `Invitation email changed to ${updated.inviteeEmail}`
      });
      this.invitationsResource.reload();
    } catch (error) {
      this.messageService.add({ severity: 'error', summary: 'Update failed', detail: this.errorText(error) });
    } finally {
      this.processingAction.set(false);
    }
  }

  getStatusSeverity(status: InvitationStatus): 'info' | 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case InvitationStatus.Pending:
        return 'info';
      case InvitationStatus.Accepted:
        return 'success';
      case InvitationStatus.Declined:
      case InvitationStatus.Cancelled:
        return 'warn';
      case InvitationStatus.Expired:
        return 'danger';
      case InvitationStatus.PendingPurchase:
      default:
        return 'secondary';
    }
  }

  // No 'info': it was Guardian's, and Guardian is not a relationship any more. A union wider than the
  // switch reads as a case somebody forgot to write.
  getHouseholdRelationshipSeverity(householdRelationship: HouseholdRelationship): 'success' | 'warn' | 'secondary' {
    switch (householdRelationship) {
      case HouseholdRelationship.Owner:
        return 'warn';
      case HouseholdRelationship.Member:
        return 'success';
      case HouseholdRelationship.Dependent:
      default:
        return 'secondary';
    }
  }

  formatDate(date: string | null | undefined): string {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  }

  formatDateTime(date: string | null | undefined): string {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  }

  formatBool(value: boolean | null | undefined): string {
    if (value === null || value === undefined) return 'N/A';
    return value ? 'Yes' : 'No';
  }

  private matchesGroup(inv: InvitationResponseV1, query: string): boolean {
    return (inv.groupName ?? '').toLowerCase().includes(query) || inv.groupId.toLowerCase().includes(query);
  }

  private matchesInviter(inv: InvitationResponseV1, query: string): boolean {
    return (inv.inviterName ?? '').toLowerCase().includes(query) || inv.inviterUserId.toLowerCase().includes(query);
  }

  private matchesSearch(inv: InvitationResponseV1, search: string): boolean {
    return (
      inv.inviteeEmail.toLowerCase().includes(search) ||
      (inv.inviterName ?? '').toLowerCase().includes(search) ||
      (inv.groupName ?? '').toLowerCase().includes(search) ||
      (inv.inviteeFirstName ?? '').toLowerCase().includes(search) ||
      (inv.inviteeLastName ?? '').toLowerCase().includes(search)
    );
  }

  private errorText(error: unknown): string {
    if (error instanceof Error) return error.message;
    return 'Unexpected error';
  }
}
